/**
 * Data extraction utility functions for the admin panel
 * These functions help safely extract data from nested objects
 * and provide consistent data access across different components
 */

/**
 * Safely access nested properties from an object
 * @param {Object} obj - The object to access
 * @param {String} path - The dot-notation path to the property
 * @param {*} defaultValue - The default value to return if property doesn't exist
 * @returns {*} The property value or defaultValue
 */
export const safeGet = (obj, path, defaultValue = null) => {
  if (!obj) return defaultValue;
  return path.split('.').reduce((prev, curr) => {
    return prev && prev[curr] !== undefined ? prev[curr] : defaultValue;
  }, obj);
};

/**
 * Extract customer name from order/booking data
 * @param {Object} data - Order or booking data
 * @returns {String} Customer name or default value
 */
export const extractCustomerName = (data) => {
  return safeGet(data, 'customerName') || 
         safeGet(data, 'userInfo.displayName') ||
         safeGet(data, 'user.displayName') ||
         safeGet(data, 'userData.displayName') ||
         (safeGet(data, 'payer.name.given_name') && 
          safeGet(data, 'payer.name.surname') && 
          `${safeGet(data, 'payer.name.given_name')} ${safeGet(data, 'payer.name.surname')}`) ||
         safeGet(data, 'paymentSource.name.given_name') ||
         safeGet(data, 'paymentSource.name.surname') &&
         `${safeGet(data, 'paymentSource.name.given_name')} ${safeGet(data, 'paymentSource.name.surname')}` ||
         safeGet(data, 'paymentSource.name.full') ||
         safeGet(data, 'shipping.name.full_name') || 
         safeGet(data, 'customer.name') ||
         safeGet(data, 'name') ||
         '未知客户';
};

/**
 * Extract customer email from order/booking data
 * @param {Object} data - Order or booking data
 * @returns {String} Customer email or default value
 */
export const extractCustomerEmail = (data) => {
  return safeGet(data, 'customerEmail') || 
         safeGet(data, 'userInfo.email') ||
         safeGet(data, 'user.email') ||
         safeGet(data, 'userData.email') ||
         safeGet(data, 'userEmail') ||  
         safeGet(data, 'payer.email_address') || 
         safeGet(data, 'paymentSource.email_address') || 
         safeGet(data, 'payment_source.paypal.email_address') || 
         safeGet(data, 'customer.email') ||
         safeGet(data, 'email') ||
         '未提供邮箱';
};

/**
 * Extract customer phone from order/booking data
 * @param {Object} data - Order or booking data
 * @returns {String} Customer phone or default value
 */
export const extractCustomerPhone = (data) => {
  return safeGet(data, 'customerPhone') || 
         safeGet(data, 'userInfo.phone') ||
         safeGet(data, 'user.phone') ||
         safeGet(data, 'userData.phone') ||
         safeGet(data, 'userData.phoneNumber') ||
         safeGet(data, 'phone') ||
         safeGet(data, 'phoneNumber') ||
         safeGet(data, 'customerPhoneNumber') ||
         safeGet(data, 'customer.phone') ||
         safeGet(data, 'shipping.phone') ||
         '未提供电话';
};

/**
 * Extract payment amount from order/booking data
 * @param {Object} data - Order or booking data
 * @returns {Number} Payment amount or 0
 */
export const extractPaymentAmount = (data) => {
  let amount = null;
  
  // Try various possible paths
  if (safeGet(data, 'total')) {
    amount = safeGet(data, 'total');
  } else if (safeGet(data, 'price')) {
    amount = safeGet(data, 'price');
  } else if (safeGet(data, 'amount')) {
    amount = safeGet(data, 'amount');
  } else if (safeGet(data, 'cost')) {
    amount = safeGet(data, 'cost');
  } else if (safeGet(data, 'totalAmount')) {
    amount = safeGet(data, 'totalAmount');
  } else if (safeGet(data, 'captureAmount.value')) {
    amount = safeGet(data, 'captureAmount.value');
  } else if (safeGet(data, 'purchase_units.0.payments.captures.0.amount.value')) {
    amount = safeGet(data, 'purchase_units.0.payments.captures.0.amount.value');
  } else if (safeGet(data, 'purchase_units.0.amount.value')) {
    amount = safeGet(data, 'purchase_units.0.amount.value');
  } else if (safeGet(data, 'amount.total')) {
    amount = safeGet(data, 'amount.total');
  } else if (safeGet(data, 'payment.amount')) {
    amount = safeGet(data, 'payment.amount');
  } else if (safeGet(data, 'paymentDetails.amount')) {
    amount = safeGet(data, 'paymentDetails.amount');
  } else if (safeGet(data, 'service.price')) {
    amount = safeGet(data, 'service.price');
  }
  
  // Convert to number
  if (amount !== null) {
    amount = parseFloat(amount);
    if (isNaN(amount)) {
      amount = 0;
    }
  } else {
    amount = 0;
  }
  
  return amount;
};

/**
 * Extract payment status from order/booking data
 * @param {Object} data - Order or booking data
 * @returns {String} Payment status or 'pending'
 */
export const extractPaymentStatus = (data) => {
  return safeGet(data, 'paymentStatus') || 
         safeGet(data, 'status') ||
         safeGet(data, 'processingStatus') ||
         safeGet(data, 'paymentSource.status') ||
         safeGet(data, 'payment.status') ||
         safeGet(data, 'paymentDetails.status') ||
         safeGet(data, 'purchase_units.0.payments.captures.0.status') ||
         'pending';
};

/**
 * Extract payment ID from order/booking data
 * @param {Object} data - Order or booking data
 * @returns {String} Payment ID or empty string
 */
export const extractPaymentId = (data) => {
  return safeGet(data, 'paymentId') || 
         safeGet(data, 'payment.id') || 
         safeGet(data, 'transactionId') ||
         safeGet(data, 'captureDetails.id') || 
         safeGet(data, 'purchase_units.0.payments.captures.0.id') ||
         '';
};

/**
 * Extract shipping address from order data
 * @param {Object} data - Order data
 * @returns {Object} Shipping address information
 */
export const extractShippingAddress = (data) => {
  let address = {};
  
  if (safeGet(data, 'shippingAddress')) {
    address = {
      name: safeGet(data, 'shippingAddress.name') || extractCustomerName(data),
      addressLine: [
        safeGet(data, 'shippingAddress.province'),
        safeGet(data, 'shippingAddress.city'),
        safeGet(data, 'shippingAddress.district'),
        safeGet(data, 'shippingAddress.address'),
      ].filter(Boolean).join(', '),
      postalCode: safeGet(data, 'shippingAddress.postalCode') || '',
      phone: safeGet(data, 'shippingAddress.phone') || extractCustomerPhone(data),
      country: safeGet(data, 'shippingAddress.country') || safeGet(data, 'countryCode') || 'SG'
    };
  } else if (safeGet(data, 'purchase_units.0.shipping')) {
    address = {
      name: safeGet(data, 'purchase_units.0.shipping.name.full_name') || extractCustomerName(data),
      addressLine: safeGet(data, 'purchase_units.0.shipping.address') ? [
        safeGet(data, 'purchase_units.0.shipping.address.address_line_1'),
        safeGet(data, 'purchase_units.0.shipping.address.address_line_2'),
        safeGet(data, 'purchase_units.0.shipping.address.admin_area_2'),
        safeGet(data, 'purchase_units.0.shipping.address.admin_area_1')
      ].filter(Boolean).join(', ') : '',
      postalCode: safeGet(data, 'purchase_units.0.shipping.address.postal_code') || '',
      phone: extractCustomerPhone(data),
      country: safeGet(data, 'purchase_units.0.shipping.address.country_code') || safeGet(data, 'countryCode') || 'SG'
    };
  } else if (safeGet(data, 'shipping')) {
    if (typeof safeGet(data, 'shipping.address') === 'string') {
      address = {
        name: safeGet(data, 'shipping.name') || extractCustomerName(data),
        addressLine: safeGet(data, 'shipping.address'),
        phone: safeGet(data, 'shipping.phone') || extractCustomerPhone(data),
        country: safeGet(data, 'shipping.country_code') || safeGet(data, 'countryCode') || 'SG'
      };
    } else {
      address = {
        name: safeGet(data, 'shipping.name.full_name') || safeGet(data, 'shipping.name') || extractCustomerName(data),
        addressLine: [
          safeGet(data, 'shipping.address.address_line_1'),
          safeGet(data, 'shipping.address.admin_area_2'),
          safeGet(data, 'shipping.address.admin_area_1')
        ].filter(Boolean).join(', '),
        postalCode: safeGet(data, 'shipping.address.postal_code') || '',
        phone: safeGet(data, 'shipping.phone') || extractCustomerPhone(data),
        country: safeGet(data, 'shipping.address.country_code') || safeGet(data, 'countryCode') || 'SG'
      };
    }
  } else if (safeGet(data, 'address')) {
    address = {
      name: extractCustomerName(data),
      addressLine: typeof safeGet(data, 'address') === 'string' ? 
                  safeGet(data, 'address') : 
                  JSON.stringify(safeGet(data, 'address')),
      phone: extractCustomerPhone(data),
      country: safeGet(data, 'countryCode') || 'SG'
    };
  } else if (safeGet(data, 'userInfo.address')) {
    address = {
      name: safeGet(data, 'userInfo.displayName') || extractCustomerName(data),
      addressLine: safeGet(data, 'userInfo.address'),
      phone: safeGet(data, 'userInfo.phone') || extractCustomerPhone(data),
      country: safeGet(data, 'countryCode') || 'SG'
    };
  } else {
    address = {
      name: extractCustomerName(data),
      addressLine: '',
      phone: extractCustomerPhone(data),
      country: safeGet(data, 'countryCode') || 'SG'
    };
  }
  
  return address;
};

/**
 * Format price for display
 * @param {Number|String} price - The price value
 * @returns {String} Formatted price string
 */
export const formatPrice = (price) => {
  // Check if price is valid
  if (price === undefined || price === null) {
    return 'S$0.00';
  }
  
  // Convert to number
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  // Validate conversion
  if (isNaN(numericPrice)) {
    return 'S$0.00';
  }
  
  return `S$${numericPrice.toFixed(2)}`;
};

/**
 * Format date from Firebase timestamp or other date formats
 * @param {Object|String|Number} timestamp - The date to format
 * @returns {String} Formatted date string
 */
export const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString('zh-CN');
    }
    
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString('zh-CN');
    }
    
    if (typeof timestamp === 'string') {
      // Check if this is an ISO string or similar
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('zh-CN');
      }
      return timestamp; // Return as is if it's not a valid date string
    }
    
    return new Date(timestamp).toLocaleString('zh-CN');
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(timestamp);
  }
};

/**
 * Format time slots array or string
 * @param {Array|String} timeSlots - The time slots
 * @returns {String} Formatted time slots
 */
export const formatTimeSlots = (timeSlots) => {
  if (!timeSlots) return 'N/A';
  
  if (Array.isArray(timeSlots)) {
    return timeSlots.join(', ');
  }
  
  return String(timeSlots);
};

/**
 * Get status chip color based on status
 * @param {String} status - The status value
 * @returns {String} Material UI color name
 */
export const getStatusColor = (status) => {
  if (!status) return 'default';
  
  const statusLower = status.toLowerCase();
  
  if (statusLower === 'completed' || statusLower === 'paid') {
    return 'success';
  }
  
  if (statusLower === 'delivered') {
    return 'warning';
  }
  
  if (statusLower === 'shipped') {
    return 'primary';
  }
  
  if (statusLower === 'pending') {
    return 'warning';
  }
  
  if (statusLower === 'processing' || statusLower === 'confirmed') {
    return 'default';
  }
  
  if (statusLower === 'cancelled' || statusLower === 'refunded') {
    return 'error';
  }
  
  return 'default';
};

/**
 * Extract standardized items from orders or bookings
 * @param {Object} data - Order or booking data
 * @returns {Array} Standardized items array
 */
export const extractItems = (data) => {
  if (safeGet(data, 'items') && Array.isArray(data.items)) {
    return data.items.map(item => ({
      name: item.name || 'Unknown Item',
      quantity: item.quantity || 1,
      price: item.price || 0,
      total: (item.price || 0) * (item.quantity || 1)
    }));
  }
  
  // For bookings, create a single item
  if (safeGet(data, 'fortuneTellerId') || safeGet(data, 'fortuneTellerName')) {
    const price = extractPaymentAmount(data);
    return [{
      name: safeGet(data, 'fortuneTellerName') || 
            safeGet(data, 'service.name') || 
            safeGet(data, 'service.providerName') || 
            '预约服务',
      quantity: 1,
      price: price,
      total: price
    }];
  }
  
  return [];
};